import React from 'react'
import { isEmpty, isNil } from 'lodash'
import { IconExternalLink } from '@tabler/icons-react'

import { Radio } from '@/components/forms/Checkbox'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'
import { TextArea } from '@/components/forms/TextArea'
import {
  CARTESIA_DASHBOARD_URL,
  PE_AGENT_ID_PATTERN,
  SLUG_PATTERN,
} from '@/reducers/agentProvisioning/agentProvisioning.constants'

const BACKEND_CODE_QUESTION = 'Does this agent have its own backend code?'

// Final step — the voice_agents master row. "Create agent" provisions
// atomically; rejections (slug_taken, slug_reserved_shape, …) render inline
// below the fields.
//
// The slug comes last because it can depend on the prompt config. The question
// is whether the agent has its own backend code (a code router), not whether it
// has a ROUTER_NAME env var: many agents fall back to a slug written in their
// code, so the env var can be missing on an agent that needs its slug. If yes,
// the operator types the slug its code calls (Cartesia's API never exposes
// env-var values, so nobody can derive it); one whose code isn't released on
// this environment needs a confirm. If no, no slug is sent and the BE generates
// a neutral one from the prompt config ID (AVR-836/837). Step 1's validate
// reports whether the agent has a ROUTER_NAME env var (`routerNameSet`); an
// answer that disagrees gets a hint, never a block.
//
// Balto admins (org 1) additionally get the "Prompt config" section (AVR-559):
// seeding is always on — the agent_prompt_configs row lands in the same
// provision transaction, an empty shell by default or the full config JSON
// pasted in the DB format — so no manual SQL seed is needed afterwards. The
// slug-source choice lives only where that section does, since the generated
// slug needs the seed.
export const AgentRowStep = ({
  slug,
  noBackendCode,
  routerNameSet,
  useCaseName,
  agentId,
  onChange,
  provisionError,
  showPromptSeed,
  promptConfigAgentId,
  promptConfigJson,
  promptConfigJsonError,
  peAgentIdFromJson,
}) => {
  const trimmedSlug = slug.trim()
  const slugFormatInvalid = !isEmpty(trimmedSlug) && !SLUG_PATTERN.test(trimmedSlug)
  // Strict comparisons: null means the BE couldn't read the env vars.
  const backendCodeHint =
    noBackendCode && routerNameSet === true
      ? 'Cartesia shows a ROUTER_NAME on this agent, which usually means it has backend code.'
      : !noBackendCode && routerNameSet === false
        ? 'Cartesia shows no ROUTER_NAME on this agent. Check that its code calls this slug.'
        : null
  // When a pasted config carries its own agent_id, that key wins — the ID field
  // mirrors it read-only so the operator sees what will be seeded.
  const effectivePeAgentId = peAgentIdFromJson ?? promptConfigAgentId
  const peIdFormatInvalid =
    !isEmpty(effectivePeAgentId.trim()) && !PE_AGENT_ID_PATTERN.test(effectivePeAgentId.trim())

  const envVarsLink = (
    <a
      className="create-agent__external-link"
      href={`${CARTESIA_DASHBOARD_URL}/${agentId}?tab=environment-variables`}
      target="_blank"
      rel="noreferrer"
    >
      Cartesia environment variables <IconExternalLink size={14} />
    </a>
  )

  // `withLink` is false inside the question, which already links the env vars.
  const slugField = (withLink) => (
    <FormField>
      <label htmlFor="agent-slug">ROUTER_NAME</label>
      <TextInput
        id="agent-slug"
        value={slug}
        placeholder="brave-curie"
        onChange={(event) => onChange('slug', event.target.value)}
      />
      {slugFormatInvalid ? (
        <div className="form-caption create-agent__caption-error">
          Use lowercase letters, digits, and hyphens only — underscores are not allowed.
        </div>
      ) : (
        <div className="form-caption">
          {withLink ? (
            <>
              The slug its code calls. Copy it from the agent&apos;s {envVarsLink}; some agents set
              it in code instead.
            </>
          ) : (
            <>
              The slug its code calls: usually its <code>ROUTER_NAME</code> on Cartesia, though some
              agents set it in code.
            </>
          )}
        </div>
      )}
    </FormField>
  )

  return (
    <div className="create-agent__step" data-testid="agent-row-step">
      <h2>Agent details</h2>

      <div className="field-stack">
        <FormField>
          <label htmlFor="agent-use-case">Use case</label>
          <TextInput
            id="agent-use-case"
            value={useCaseName}
            placeholder="Appointment reminders"
            onChange={(event) => onChange('useCaseName', event.target.value)}
          />
          <div className="form-caption">A short label for what this agent does.</div>
        </FormField>

        {showPromptSeed && (
          <>
            <FormField>
              <label htmlFor="prompt-config-id">Prompt config ID</label>
              <TextInput
                id="prompt-config-id"
                value={effectivePeAgentId}
                placeholder="customer/agent_name"
                disabled={!isNil(peAgentIdFromJson)}
                onChange={(event) => onChange('promptConfigAgentId', event.target.value)}
              />
              <div
                className={
                  peIdFormatInvalid ? 'form-caption create-agent__caption-error' : 'form-caption'
                }
              >
                {peIdFormatInvalid ? (
                  <>
                    The ID must look like <code>customer/agent</code> (lowercase letters, digits,
                    hyphens, underscores).
                  </>
                ) : !isNil(peAgentIdFromJson) ? (
                  <>
                    Taken from the pasted config&apos;s <code>agent_id</code>.
                  </>
                ) : (
                  <>
                    The PE key the config is stored under — matches the <code>agent_id</code> in the
                    agent&apos;s <code>prompt_config.yaml</code>.
                  </>
                )}
              </div>
            </FormField>

            <FormField>
              <label htmlFor="prompt-config-json">Config JSON (optional)</label>
              <TextArea
                id="prompt-config-json"
                className="create-agent__json-input"
                value={promptConfigJson}
                rows={8}
                placeholder='{ "agent_id": "customer/agent_name", "nodes": [...], ... }'
                onChange={(event) => onChange('promptConfigJson', event.target.value)}
              />
              <div className="form-caption">
                Leave empty to seed an <strong>empty shell</strong>. To seed a real config, paste
                the full JSON exactly as stored in <code>agent_prompt_configs.config</code>.
              </div>
              {promptConfigJsonError && (
                <div className="form-caption create-agent__json-error">{promptConfigJsonError}</div>
              )}
            </FormField>
          </>
        )}

        {showPromptSeed ? (
          // Yes reveals the ROUTER_NAME field directly beneath it; No only
          // carries its caption.
          <FormField
            label={BACKEND_CODE_QUESTION}
            role="radiogroup"
            aria-label={BACKEND_CODE_QUESTION}
          >
            <div className="form-caption">
              A <code>ROUTER_NAME</code> in its {envVarsLink} usually means yes.
            </div>
            <div className="field-stack">
              <Radio
                id="backend-code-yes"
                name="has-backend-code"
                label="Yes"
                checked={!noBackendCode}
                onChange={() => onChange('noBackendCode', false)}
              />
              {!noBackendCode && slugField(false)}

              <Radio
                id="backend-code-no"
                name="has-backend-code"
                label="No"
                checked={noBackendCode}
                onChange={() => onChange('noBackendCode', true)}
              />
              <div className="form-caption">
                A neutral slug is generated from the prompt config ID, the same one on every
                environment.
              </div>
            </div>
            {backendCodeHint && <ErrorMessage warning content={backendCodeHint} />}
          </FormField>
        ) : (
          slugField(true)
        )}
      </div>

      {provisionError && <ErrorMessage content={provisionError} />}
    </div>
  )
}
